import { useState, useRef, useEffect, useMemo } from "react";
import {
  Syringe,
  Search,
  Check,
  Info,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CircularProgress } from "@mui/material";
import useTitanApp from "../../../../hooks/useTitanApp";
import {
  addVaccination,
  setDrugsAboutData,
} from "../../../../dispatchers/nmsDispatchers";
import {
  updateConsultationActions,
  setShowVaccineConfirmationModal,
  setVaccinationImageBase64,
  setIncrementVaccineCompleted,
  setVaccineDetails,
  trackServiceBilling,
  setGeneratedVaccinationCertificate,
  setAddPatientAIChatMessage,
  SaveAIChatMessage,
  deletePrescriptionDraft,
  getPatientConsultationByConsultationId,
} from "../../../../dispatchers/patientDispatchers";
import { ActionType } from "../../../../enum/ActionType";
import { generateVaccinationImageBase64 } from "./generateVaccinationImage";
import removeActionFromLocal from "../../../../utils/removeFromLocalStorage";
import { v4 as uuidv4 } from "uuid";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import { getPharmacyContactDetails } from "../../../../dispatchers/pharmacyServiceDispatchers";
import { getCompanyVaccineByCompanyId } from "@/dispatchers/nmsDispatchers";
import InputMask from "react-input-mask";

interface VaccineItem {
  id: string;
  vaccineId: string;
  dose: string;
  schedule: string;
  route: string;
  site: string;
  batchNumber: string;
  expiryDate: string;
  nextDueDate: string;
  collapsed?: boolean;
}

const scheduleOptions = [
  "Initial Standard",
  "Booster",
  "Catch-up",
  "Travel",
  "High Risk",
  "Accelerated",
];

const routes = [
  "IM / SC",
  "IM (Intramuscular)",
  "SC (Subcutaneous)",
  "Oral",
  "Intranasal",
  "Intradermal",
];

const siteOptions = [
  "Deltoid - Left",
  "Deltoid - Right",
  "Thigh - Left",
  "Thigh - Right",
  "Gluteal - Left",
  "Gluteal - Right",
  "Anterolateral Thigh - Left",
  "Anterolateral Thigh - Right",
  "Ventrogluteal - Left",
  "Ventrogluteal - Right",
];

const isValidDateDDMMYYYY = (dateString: string): boolean => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateString);
  if (!match) return false;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const VaccinationNewUI = ({ handleClose }) => {
  const { appState, nmsServiceState, patientState } = useTitanApp();
  const { companyVaccines } = nmsServiceState || {};
  const {
    selectedPatientId,
    selectedPharmacyId,
    selectedPatientDetails,
    vaccineDetails,
    consultationId,
    selectedPatientConsultation,
    patientAIChatMessages,
    consultationData,
  } = patientState || {};
  const {
    darkMode,
    currentPharmacy,
    currentUser,
    company,
    currentPharmacyId,
    companyId,
  } = appState || {};

  const isConsultationCompleted =
    selectedPatientConsultation?.consultationStatus === 1;

  const [vaccineItems, setVaccineItems] = useState<VaccineItem[]>([
    {
      id: uuidv4(),
      vaccineId: "",
      dose: "",
      schedule: "Initial Standard",
      route: "IM / SC",
      site: "",
      batchNumber: "",
      expiryDate: "",
      nextDueDate: "",
      collapsed: false,
    },
  ]);

  const [vaccineSearchOpen, setVaccineSearchOpen] = useState<
    Record<string, boolean>
  >({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {},
  );
  const [showVaccineInfo, setShowVaccineInfo] = useState<
    Record<string, boolean>
  >({});
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [touchedFields, setTouchedFields] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const flattenedProducts = useMemo(() => {
    const results = [];
    const seenIds = new Set();
    (companyVaccines || []).forEach((item: any) => {
      const vaccine =
        item?.value && typeof item.value === "object" ? item.value : item;
      if (vaccine && vaccine.vaccineName) {
        (vaccine.products || []).forEach((product: any) => {
          const uniqueId = `${vaccine._id}-${product.productId}`;
          if (!seenIds.has(uniqueId)) {
            results.push({
              ...vaccine,
              selectedProduct: product,
              uniqueId,
            });
            seenIds.add(uniqueId);
          }
        });
      }
    });
    return results;
  }, [companyVaccines]);

  const filterProducts = (queryRaw: string) => {
    const query = (queryRaw || "").toLowerCase().trim();
    if (!query) return flattenedProducts;

    const terms = query.split(/\s+/).filter(Boolean);
    if (terms.length === 0) return flattenedProducts;

    return flattenedProducts.filter((item: any) => {
      const vaccineName = (item.vaccineName || "").toLowerCase();
      const productName = (
        item.selectedProduct?.productName || ""
      ).toLowerCase();
      const manufacturerName = (
        item.selectedProduct?.manufacturerName || ""
      ).toLowerCase();

      const searchString = `${vaccineName} ${productName} ${manufacturerName}`;

      return terms.every((term) => searchString.includes(term));
    });
  };

  const getSelectedProduct = (vaccineId: string) => {
    return flattenedProducts.find((v: any) => v.uniqueId === vaccineId);
  };

  const getMatchedProductForDraftName = (draftName: string) => {
    if (!draftName) return undefined;

    const normalizedDraft = draftName.toLowerCase().trim();

    // Exact full match: vaccineName - productName
    const fullMatch = flattenedProducts.find((product: any) => {
      const fullName = `${product.vaccineName || ""} - ${product.selectedProduct
        ?.productName || ""}`.toLowerCase();
      return fullName === normalizedDraft;
    });
    if (fullMatch) return fullMatch;

    // Exact name-only matches first
    const vaccineNameMatch = flattenedProducts.find((product: any) => {
      const vaccineName = (product.vaccineName || "").toLowerCase();
      const productName = (
        product.selectedProduct?.productName || ""
      ).toLowerCase();
      return (
        vaccineName === normalizedDraft ||
        productName === normalizedDraft ||
        (normalizedDraft.includes(vaccineName) && vaccineName.length > 0) ||
        (normalizedDraft.includes(productName) && productName.length > 0)
      );
    });
    if (vaccineNameMatch) return vaccineNameMatch;

    // Partial matching
    return flattenedProducts.find((product: any) => {
      const vaccineName = (product.vaccineName || "").toLowerCase();
      const productName = (
        product.selectedProduct?.productName || ""
      ).toLowerCase();
      return (
        (vaccineName && normalizedDraft.includes(vaccineName)) ||
        (productName && normalizedDraft.includes(productName)) ||
        (vaccineName && vaccineName.includes(normalizedDraft)) ||
        (productName && productName.includes(normalizedDraft))
      );
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(dropdownRefs.current).forEach((itemId) => {
        const ref = dropdownRefs.current[itemId];
        if (ref && !ref.contains(event.target as Node)) {
          setVaccineSearchOpen((prev) => ({ ...prev, [itemId]: false }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    getCompanyVaccineByCompanyId(companyId);
  }, []);

  // Handle pre-filling from draft data
  useEffect(() => {
    if (
      !vaccineDetails?.isDraft ||
      !vaccineDetails?.allVaccines?.length ||
      flattenedProducts.length === 0
    )
      return;

    const expandedIndex: number = vaccineDetails.expandedIndex ?? 0;

    const builtItems: VaccineItem[] = vaccineDetails.allVaccines.map(
      (dv: any, idx: number) => {
        const draftName = (dv.vaccineName || "").toLowerCase();

        // Try to match against pharmacy vaccine catalogue
        const matchedProduct = flattenedProducts.find((product: any) => {
          const vaccineName = (product.vaccineName || "").toLowerCase();
          const productName = (
            product.selectedProduct?.productName || ""
          ).toLowerCase();
          const fullName = `${vaccineName} - ${productName}`;
          return (
            draftName.includes(vaccineName) ||
            draftName.includes(productName) ||
            draftName === fullName
          );
        });

        const matchedProductByDraft = getMatchedProductForDraftName(draftName);

        return {
          id: uuidv4(),
          vaccineId: matchedProductByDraft
            ? matchedProductByDraft.uniqueId
            : "",
          dose: dv.dose || (matchedProductByDraft?.dosage ?? ""),
          schedule: dv.schedule || "Initial Standard",
          route: dv.route || (matchedProductByDraft?.route ?? "IM / SC"),
          site: "Deltoid - Left",
          batchNumber: "",
          expiryDate: "",
          nextDueDate: "",
          collapsed: idx !== expandedIndex, // only expand the clicked one
        };
      },
    );

    if (builtItems.length > 0) {
      setVaccineItems(builtItems);
    }
  }, [vaccineDetails, flattenedProducts]);

  const handleActionSave = async (vaccines) => {
    if (isConsultationCompleted) return;
    const vaccinationPayload = {
      payload: JSON.stringify({
        vaccinationDetails: vaccines,
        recordedAt: new Date().toISOString(),
      }),
      actionName: ActionType[6],
      actionNumber: "6",
      isPerformed: true,
    };
    try {
      await updateConsultationActions({
        consultationId: consultationId,
        payload: vaccinationPayload,
      });
      const vaccineMessage = [
        {
          sender: "success",
          message: "Vaccine administered successfully.",
          actionId: 6,
          details: vaccinationPayload,
        },
      ];
      await SaveAIChatMessage({
        ConsultationChat: JSON.stringify(vaccineMessage),
        ConsultationId: consultationId,
      });
      setAddPatientAIChatMessage({
        sender: "success",
        message: "Vaccine administered successfully.",
        actionId: 6,
        details: vaccinationPayload,
      });

      const pendingDraft =
        patientAIChatMessages?.find(
          (msg: any) =>
            (msg.actionId === 12 || msg.actionId === "12") &&
            msg.sender === "success",
        ) ||
        (() => {
          if (consultationData?.consultationChat) {
            try {
              const parsed = JSON.parse(consultationData.consultationChat);
              return parsed.find(
                (msg: any) =>
                  (msg.actionNumber === 12 || msg.actionNumber === "12") &&
                  msg.sender === "success",
              );
            } catch (e) {
              console.error("Invalid consultationChat JSON", e);
            }
          }
          return null;
        })() ||
        consultationData?.actions?.find(
          (action: any) => action.actionNumber === "12",
        );

      if (pendingDraft) {
        try {
          await deletePrescriptionDraft({
            consultationID: consultationId,
            actionNumber: "12",
          });

          await getPatientConsultationByConsultationId(consultationId);
        } catch (err) {
          console.error("Error deleting vaccine draft:", err);
        }
      }

      setVaccineDetails(vaccinationPayload);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddVaccineItem = () => {
    const collapsedExisting = vaccineItems.map((item) => ({
      ...item,
      collapsed: true,
    }));

    const newItem: VaccineItem = {
      id: uuidv4(),
      vaccineId: "",
      dose: "",
      schedule: "Initial Standard",
      route: "IM / SC",
      site: "",
      batchNumber: "",
      expiryDate: "",
      nextDueDate: "",
      collapsed: false,
    };

    setVaccineItems([...collapsedExisting, newItem]);
  };

  const handleUpdateVaccineItem = (
    id: string,
    updates: Partial<VaccineItem>,
  ) => {
    setVaccineItems(
      vaccineItems.map((item) =>
        item.id === id ? { ...item, ...updates } : item,
      ),
    );
  };

  const handleToggleCollapse = (id: string) => {
    setVaccineItems(
      vaccineItems.map((item) =>
        item.id === id ? { ...item, collapsed: !item.collapsed } : item,
      ),
    );
  };

  const handleRemoveVaccineItem = (id: string) => {
    if (vaccineItems.length === 1) return;
    setVaccineItems(vaccineItems.filter((item) => item.id !== id));
  };

  const areAllVaccinesComplete = () => {
    return vaccineItems.every(
      (item) =>
        item.vaccineId &&
        item.dose &&
        item.schedule &&
        item.route &&
        (item.route === "Oral" || item.site) &&
        item.batchNumber &&
        item.expiryDate,
    );
  };

  const getFieldError = (
    item: VaccineItem,
    field: keyof VaccineItem,
  ): string | null => {
    if (!touchedFields[item.id]?.[field]) {
      return null;
    }

    if (!item[field]) {
      switch (field) {
        case "vaccineId":
          return "Vaccine selection is required";
        case "dose":
          return "Dose is required";
        case "schedule":
          return "Schedule is required";
        case "route":
          return "Route is required";
        case "site":
          // Site is only required if route is not Oral
          if (item.route !== "Oral") {
            return "Site is required";
          }
          return null;
        case "batchNumber":
          return "Batch number is required";
        case "expiryDate":
          return "Expiry date is required";
        default:
          return null;
      }
    }

    if (field === "nextDueDate" && item.nextDueDate) {
      if (!isValidDateDDMMYYYY(item.nextDueDate)) {
        return "Next due date must be in DD/MM/YYYY format if being entered.";
      }
    }

    return null;
  };

  const hasInvalidNextDueDate = () => {
    const hasInvalidNextDueDate = vaccineItems.some(
      (item) => item.nextDueDate && !isValidDateDDMMYYYY(item.nextDueDate),
    );
    return hasInvalidNextDueDate;
  };

  const handleAdministerVaccines = async () => {
    if (isConsultationCompleted) {
      console.error("Consultation is already completed");
      return;
    }

    if (buttonsDisabled) {
      return;
    }

    if (hasInvalidNextDueDate()) {
      alert("Next due date must be in DD/MM/YYYY format if entered.");
      return;
    }

    if (!areAllVaccinesComplete()) {
      alert("Please fill in all required fields for each vaccine");
      return;
    }

    setButtonsDisabled(true);

    try {
      const contactDetails = await getPharmacyContactDetails(
        selectedPharmacyId,
      );

      const vaccines = vaccineItems.map((item) => {
        const selectedProduct = getSelectedProduct(item.vaccineId);

        // Validate that selectedProduct exists (shouldn't happen due to areAllVaccinesComplete check)
        if (!selectedProduct) {
          throw new Error(`Product not found for vaccine item ${item.id}`);
        }

        const product = selectedProduct.selectedProduct || {};

        return {
          vaccineName: selectedProduct.vaccineName,
          prescriptionItemId: product.prescribedItemId || "",
          dose: item.dose,
          schedule: item.schedule,
          site: item.route === "Oral" ? "" : item.site,
          manufacturer: product.productName || product.manufacturerName || "",
          batchNumber: item.batchNumber,
          expiryDate: item.expiryDate,
          nextDueDate: item.nextDueDate || "",
          note: "",
          route: item.route,
        };
      });

      const vaccinationModel = {
        prescriptionId: uuidv4(),
        patientId: selectedPatientId,
        pharmacyID: selectedPharmacyId,
        // nextDueDate is only kept for the consultation action; the API doesn't accept it.
        vaccines: vaccines.map(({ nextDueDate, ...vaccine }) => vaccine),
        consultationId: consultationId,
      };

      let response;
      try {
        response = await addVaccination(vaccinationModel);
      } catch (saveError) {
        console.error("Save vaccination error:", saveError);
        throw new Error("Failed to save vaccination record");
      }

      if (response === true) {
        setGeneratedVaccinationCertificate(null);
        // Save action
        try {
          await handleActionSave({ vaccinations: vaccines });
        } catch (actionError) {
          console.error("Action save error:", actionError);
          // Don't throw here - continue with other operations
        }

        // Update UI state
        setIncrementVaccineCompleted(true);

        // Close modal
        if (handleClose) {
          handleClose();
        }

        // Cleanup
        removeActionFromLocal(consultationId, "6");

        // Track events
        try {
          trackFirstTimeIntercomEvent({
            eventName: "vaccination_record_created",
            user: {
              id: currentUser.emailAddress,
              email: currentUser.emailAddress,
              name: currentUser.fullName,
            },
            properties: {
              source: "add_vaccination",
              vaccineCount: vaccines.length,
            },
          });
        } catch (trackError) {
          console.error("Tracking error:", trackError);
          // Non-critical error, don't throw
        }

        // Track billing
        try {
          await trackServiceBilling({
            companyId,
            consultationId,
            pharmacyId: currentPharmacyId,
            actiontype: "vaccination",
          });
        } catch (billingErr) {
          console.error("Error calling billing tracker:", billingErr);
          // Non-critical error, don't throw
        }
      } else {
        throw new Error("Failed to add vaccination - unexpected response");
      }
    } catch (error) {
      console.error("Vaccination error:", error);

      // Show user-friendly error message
      alert(
        error instanceof Error ? error.message : "An unexpected error occurred",
      );
    } finally {
      setButtonsDisabled(false);
    }
  };

  const markTouched = (itemId: string, field: keyof VaccineItem) => {
    setTouchedFields((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: true,
      },
    }));
  };

  return (
    <div className={cn("pc-actions-modal-wrapper", darkMode && "dark")}>
      <div
        className={cn(
          "p-6 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar",
          darkMode ? "bg-[#222] text-white" : "bg-white text-gray-900",
        )}
      >
        <div className="flex justify-between items-center sticky top-[-24px] z-[60] py-4 bg-inherit border-b border-transparent mb-2">
          <h3 className="flex items-center gap-2 text-xl font-semibold">
            <Syringe className="w-4 h-4 " />
            Administer Vaccine
          </h3>
          <X
            className="w-6 h-6 opacity-50 cursor-pointer hover:opacity-100"
            onClick={() => {
              handleClose();
              setDrugsAboutData([]);
            }}
          />
        </div>

        {/* Vaccine Items */}
        {vaccineItems.map((item, index) => {
          const selectedProduct = getSelectedProduct(item.vaccineId);
          const searchQuery = searchQueries[item.id] || "";
          const searchedProducts = filterProducts(searchQuery);
          const isCollapsed = item.collapsed;

          return (
            <div
              key={item.id}
              className={`p-4 space-y-4 border rounded-lg ${
                index !== 0 ? "mt-3" : ""
              } `}
            >
              {/* Header with collapse toggle */}
              <div className="flex items-center justify-between">
                <h3 className="font-medium">
                  Vaccine {index + 1}
                  {(selectedProduct ||
                    vaccineDetails?.allVaccines?.[index]?.vaccineName) && (
                    <span className="ml-2 text-sm font-normal opacity-60">
                      -{" "}
                      {selectedProduct?.vaccineName ||
                        vaccineDetails?.allVaccines?.[index]?.vaccineName}
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-1">
                  {vaccineItems.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveVaccineItem(item.id)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleCollapse(item.id)}
                  >
                    {isCollapsed ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronUp className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {!isCollapsed && (
                <div className="space-y-6">
                  {/* Vaccine Search */}
                  <div className="relative z-50 space-y-2">
                    <Label className="text-sm font-medium">
                      Search Vaccine (by disease or brand)
                    </Label>
                    <div className="flex gap-2">
                      <div
                        className="relative flex-1"
                        ref={(el) => (dropdownRefs.current[item.id] = el)}
                      >
                        <div className="relative">
                          <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                          <Input
                            placeholder="Type to search (e.g., Hepatitis A or Avaxim)..."
                            value={
                              vaccineSearchOpen[item.id]
                                ? searchQueries[item.id] || ""
                                : selectedProduct
                                ? `${selectedProduct.vaccineName} - ${selectedProduct.selectedProduct?.productName}`
                                : ""
                            }
                            onChange={(e) => {
                              setSearchQueries((prev) => ({
                                ...prev,
                                [item.id]: e.target.value,
                              }));
                              setVaccineSearchOpen((prev) => ({
                                ...prev,
                                [item.id]: true,
                              }));
                            }}
                            onFocus={() => {
                              setVaccineSearchOpen((prev) => ({
                                ...prev,
                                [item.id]: true,
                              }));
                              markTouched(item.id, "vaccineId");
                            }}
                            onBlur={() => markTouched(item.id, "vaccineId")}
                            className={cn(
                              "pl-9 h-11",
                              darkMode
                                ? "bg-gray-800 border-gray-700"
                                : "bg-gray-50",
                              getFieldError(item, "vaccineId") &&
                                "border-red-500 focus-visible:ring-red-500",
                            )}
                          />
                        </div>

                        {vaccineSearchOpen[item.id] && (
                          <div
                            className={cn(
                              "absolute w-full mt-1 border rounded-md shadow-lg max-h-[300px] overflow-auto z-50",
                              darkMode
                                ? "bg-gray-800 border-gray-700"
                                : "bg-white border-gray-200",
                            )}
                          >
                            {searchedProducts?.length === 0 ? (
                              <div className="py-6 text-sm text-center text-gray-500">
                                No vaccines/products found.
                              </div>
                            ) : (
                              <div className="py-1">
                                {searchedProducts?.map((product) => (
                                  <button
                                    key={product.uniqueId}
                                    type="button"
                                    onClick={() => {
                                      handleUpdateVaccineItem(item.id, {
                                        vaccineId: product.uniqueId,
                                      });
                                      setVaccineSearchOpen((prev) => ({
                                        ...prev,
                                        [item.id]: false,
                                      }));
                                      setSearchQueries((prev) => ({
                                        ...prev,
                                        [item.id]: "",
                                      }));
                                      markTouched(item.id, "vaccineId");
                                    }}
                                    className={cn(
                                      "w-full flex items-center px-4 py-3 text-sm text-left transition-colors",
                                      darkMode
                                        ? "hover:bg-gray-700"
                                        : "hover:bg-gray-100",
                                      item.vaccineId === product.uniqueId &&
                                        (darkMode
                                          ? "bg-blue-900/30"
                                          : "bg-blue-50"),
                                    )}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4 text-blue-600",
                                        item.vaccineId === product.uniqueId
                                          ? "opacity-100"
                                          : "opacity-0",
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        {product.vaccineName}
                                      </span>
                                      <span className="text-xs opacity-50">
                                        {product.selectedProduct?.productName}
                                        {product.selectedProduct
                                          ?.manufacturerName &&
                                          ` - ${product.selectedProduct?.manufacturerName}`}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {selectedProduct && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            setShowVaccineInfo((prev) => ({
                              ...prev,
                              [item.id]: !prev[item.id],
                            }))
                          }
                          className="shrink-0 h-11 w-11"
                        >
                          {showVaccineInfo[item.id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                    {getFieldError(item, "vaccineId") && (
                      <p className="text-sm text-red-500">
                        {getFieldError(item, "vaccineId")}
                      </p>
                    )}

                    {/* Expandable Info Panel */}
                    {selectedProduct && showVaccineInfo[item.id] && (
                      <div
                        className={cn(
                          "mt-4 p-4 border rounded-lg space-y-4 max-h-[400px] overflow-y-auto",
                          darkMode
                            ? "bg-blue-900/10 border-blue-900/30"
                            : "bg-blue-50 border-blue-200",
                        )}
                      >
                        <h4 className="flex items-center gap-2 font-semibold text-blue-600">
                          <Info className="w-4 h-4" />
                          Vaccine Information
                        </h4>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="p-2 rounded bg-white/50 dark:bg-gray-800/50">
                            <p className="text-xs opacity-60">Brand Name</p>
                            <p className="text-sm font-medium">
                              {selectedProduct.selectedProduct?.productName ||
                                "-"}
                            </p>
                          </div>
                          <div className="p-2 rounded bg-white/50 dark:bg-gray-800/50">
                            <p className="text-xs opacity-60">Manufacturer</p>
                            <p className="text-sm font-medium">
                              {selectedProduct.selectedProduct
                                ?.manufacturerName || "-"}
                            </p>
                          </div>
                          {selectedProduct.description && (
                            <div className="col-span-full">
                              <p className="text-xs opacity-60">Description</p>
                              <p className="text-sm">
                                {selectedProduct.description}
                              </p>
                            </div>
                          )}
                        </div>

                        <Separator
                          className={
                            darkMode ? "bg-blue-900/30" : "bg-blue-200"
                          }
                        />

                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                          <div className="p-2 bg-white border border-blue-200 rounded dark:bg-gray-800 dark:border-blue-900/30">
                            <p className="text-xs opacity-60">Dosage</p>
                            <p className="text-sm font-medium">
                              {selectedProduct.dosage || "-"}
                            </p>
                          </div>
                          <div className="p-2 bg-white border border-blue-200 rounded dark:bg-gray-800 dark:border-blue-900/30">
                            <p className="text-xs opacity-60">Route</p>
                            <p className="text-sm font-medium">
                              {selectedProduct.route || "-"}
                            </p>
                          </div>
                          <div className="p-2 bg-white border border-blue-200 rounded dark:bg-gray-800 dark:border-blue-900/30">
                            <p className="text-xs opacity-60">Age Range</p>
                            <p className="text-sm font-medium">
                              {selectedProduct.ageRange || "-"}
                            </p>
                          </div>
                        </div>

                        {selectedProduct.contraindications?.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-semibold tracking-wider text-red-500 uppercase">
                              Contraindications
                            </p>
                            <ul className="space-y-1 text-sm">
                              {selectedProduct.contraindications.map(
                                (c: any, i: number) => (
                                  <li key={i} className="flex gap-2">
                                    <span className="text-red-500">•</span> {c}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Batch Number
                      </Label>
                      <Input
                        value={item.batchNumber}
                        onChange={(e) =>
                          handleUpdateVaccineItem(item.id, {
                            batchNumber: e.target.value,
                          })
                        }
                        onFocus={() => markTouched(item.id, "batchNumber")}
                        onBlur={() => markTouched(item.id, "batchNumber")}
                        maxLength={100}
                        placeholder="Enter batch number"
                        className={cn(
                          "h-11",
                          darkMode
                            ? "bg-gray-800 border-gray-700"
                            : "bg-gray-50",
                        )}
                      />
                      {getFieldError(item, "batchNumber") && (
                        <p className="text-sm text-red-500">
                          {getFieldError(item, "batchNumber")}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Dose</Label>
                      <Input
                        value={item.dose}
                        onChange={(e) =>
                          handleUpdateVaccineItem(item.id, {
                            dose: e.target.value,
                          })
                        }
                        onFocus={() => markTouched(item.id, "dose")}
                        onBlur={() => markTouched(item.id, "dose")}
                        maxLength={200}
                        placeholder="e.g., 0.5ml"
                        className={cn(
                          "h-11",
                          darkMode
                            ? "bg-gray-800 border-gray-700"
                            : "bg-gray-50",
                        )}
                      />
                      {getFieldError(item, "dose") && (
                        <p className="text-sm text-red-500">
                          {getFieldError(item, "dose")}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Schedule</Label>
                      <Select
                        value={item.schedule}
                        onValueChange={(value) =>
                          handleUpdateVaccineItem(item.id, {
                            schedule: value,
                          })
                        }
                        onOpenChange={(open) => {
                          if (open) markTouched(item.id, "schedule");
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            "h-11",
                            darkMode
                              ? "bg-gray-800 border-gray-700"
                              : "bg-gray-50",
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[9999]">
                          {scheduleOptions.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {getFieldError(item, "schedule") && (
                        <p className="text-sm text-red-500">
                          {getFieldError(item, "schedule")}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Route</Label>
                      <Select
                        value={item.route}
                        onValueChange={(value) =>
                          handleUpdateVaccineItem(item.id, { route: value })
                        }
                        onOpenChange={(open) => {
                          if (open) markTouched(item.id, "route");
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            "h-11",
                            darkMode
                              ? "bg-gray-800 border-gray-700"
                              : "bg-gray-50",
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[9999]">
                          {routes.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {getFieldError(item, "route") && (
                        <p className="text-sm text-red-500">
                          {getFieldError(item, "route")}
                        </p>
                      )}
                    </div>

                    {item.route !== "Oral" && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Site</Label>
                        <Select
                          value={item.site}
                          onValueChange={(value) =>
                            handleUpdateVaccineItem(item.id, { site: value })
                          }
                          onOpenChange={(open) => {
                            if (open) markTouched(item.id, "site");
                          }}
                        >
                          <SelectTrigger
                            className={cn(
                              "h-11",
                              darkMode
                                ? "bg-gray-800 border-gray-700"
                                : "bg-gray-50",
                            )}
                          >
                            <SelectValue placeholder="Select site" />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]">
                            {siteOptions.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {getFieldError(item, "site") && (
                          <p className="text-sm text-red-500">
                            {getFieldError(item, "site")}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Expiry Date</Label>
                      <InputMask
                        mask="99/9999"
                        placeholder="MM/YYYY"
                        value={item.expiryDate}
                        onChange={(e) => {
                          handleUpdateVaccineItem(item.id, {
                            expiryDate: e.target.value,
                          });
                          markTouched(item.id, "expiryDate");
                        }}
                        onBlur={() => markTouched(item.id, "expiryDate")}
                      >
                        {(inputProps: any) => (
                          <Input
                            {...inputProps}
                            className={cn(
                              "h-11",
                              darkMode
                                ? "bg-gray-800 border-gray-700"
                                : "bg-gray-50",
                              getFieldError(item, "expiryDate") &&
                                "border-red-500 focus-visible:ring-red-500",
                            )}
                          />
                        )}
                      </InputMask>
                      {getFieldError(item, "expiryDate") && (
                        <p className="text-sm text-red-500">
                          {getFieldError(item, "expiryDate")}
                        </p>
                      )}
                    </div>

                    <div className="w-full col-span-2 space-y-2">
                      <Label className="text-sm font-medium">
                        Next Due Date
                      </Label>
                      <InputMask
                        mask="99/99/9999"
                        placeholder="DD/MM/YYYY"
                        value={item.nextDueDate}
                        onChange={(e) => {
                          handleUpdateVaccineItem(item.id, {
                            nextDueDate: e.target.value,
                          });
                          markTouched(item.id, "nextDueDate");
                        }}
                        onBlur={() => markTouched(item.id, "nextDueDate")}
                        onFocus={() => markTouched(item.id, "nextDueDate")}
                      >
                        {(inputProps: any) => (
                          <Input
                            {...inputProps}
                            className={cn(
                              "h-11",
                              darkMode
                                ? "bg-gray-800 border-gray-700"
                                : "bg-gray-50",
                              getFieldError(item, "nextDueDate") &&
                                "border-red-500 focus-visible:ring-red-500",
                            )}
                          />
                        )}
                      </InputMask>
                      {getFieldError(item, "nextDueDate") && (
                        <p className="text-sm text-red-500">
                          {getFieldError(item, "nextDueDate")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add More Button */}
        <div className="space-y-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleAddVaccineItem}
            disabled={!areAllVaccinesComplete()}
            className="w-full h-16 mt-3 text-gray-500 border-2 border-gray-300 border-dashed hover:text-gray-700 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add more
          </Button>
          {!areAllVaccinesComplete() && (
            <p className="flex items-center justify-center gap-2 text-sm text-center text-amber-600">
              <AlertTriangle className="w-4 h-4" />
              Please complete all required fields before adding another vaccine
            </p>
          )}
        </div>

        <div className="flex gap-4 pt-6 border-t border-gray-100 dark:border-gray-800">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            className="flex-1 h-12 text-gray-500 hover:text-gray-900 dark:hover:text-white"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAdministerVaccines}
            disabled={
              buttonsDisabled ||
              isConsultationCompleted ||
              !areAllVaccinesComplete()
            }
            className="flex-1 h-12 text-white transition-all bg-black shadow-lg hover:bg-gray-900 dark:bg-white dark:text-black dark:hover:bg-gray-200"
          >
            {buttonsDisabled ? (
              <>
                <CircularProgress
                  size={16}
                  className="mr-2"
                  style={{ color: darkMode ? "#000" : "#fff" }}
                />{" "}
                Creating...
              </>
            ) : (
              "Administer Vaccine"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VaccinationNewUI;
